import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CalendarDays, Plus, Route as RouteIcon, Search, Trash2 } from "lucide-react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useTraccarSession } from "@/hooks/useTraccarSession";
import { deletePlannedRoute, fetchPlannedRoutes, type PlannedRouteStatus } from "@/lib/reseller-api";
import { formatDistance, formatDuration, ROUTE_STATUS_BADGE, ROUTE_STATUSES } from "@/lib/planned-routes";
import { fetchDevices } from "@/lib/traccar-api";

const todayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

export default function PlannedRoutes() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const { user } = useTraccarSession();
  const canEdit = !user?.readonly;
  const [statusFilter, setStatusFilter] = useState<"all" | PlannedRouteStatus>("all");

  const { data: routes = [], isLoading } = useQuery({ queryKey: ["planned-routes"], queryFn: fetchPlannedRoutes });
  const { data: devices = [] } = useQuery({ queryKey: ["devices"], queryFn: fetchDevices });
  const deviceById = useMemo(() => new Map(devices.map((device) => [device.id, device])), [devices]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return routes.filter((route) => {
      if (statusFilter !== "all" && route.status !== statusFilter) return false;
      const haystack = [route.name, route.notes, route.device_id ? deviceById.get(route.device_id)?.name : null, ...route.stops.map((stop) => stop.name)]
        .filter(Boolean).join(" ").toLowerCase();
      return !query || haystack.includes(query);
    });
  }, [deviceById, routes, search, statusFilter]);

  const today = todayString();
  const todayCount = routes.filter((route) => route.route_date === today && route.status !== "cancelled").length;
  const inProgressCount = routes.filter((route) => route.status === "in_progress").length;

  const deleteMutation = useMutation({
    mutationFn: deletePlannedRoute,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["planned-routes"] });
      toast({ title: t("routes.deleted") });
    },
    onError: (error: Error) => toast({ title: t("common.error"), description: error.message, variant: "destructive" }),
  });

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">{t("routes.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("routes.subtitle")}</p>
          </div>
          {canEdit && (
            <Button onClick={() => navigate("/routes/new")}>
              <Plus className="h-4 w-4 mr-2" />
              {t("routes.create")}
            </Button>
          )}
        </div>

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("common.total")}</p>
                <p className="text-2xl font-bold">{routes.length}</p>
              </div>
              <RouteIcon className="h-5 w-5 text-primary" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("routes.today")}</p>
                <p className="text-2xl font-bold">{todayCount}</p>
              </div>
              <CalendarDays className="h-5 w-5 text-blue-500" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("routes.status.in_progress")}</p>
                <p className="text-2xl font-bold">{inProgressCount}</p>
              </div>
              <RouteIcon className="h-5 w-5 text-amber-500" />
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="relative w-full md:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder={t("routes.searchPlaceholder")} value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" />
              </div>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as "all" | PlannedRouteStatus)}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("routes.allStatuses")}</SelectItem>
                  {ROUTE_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{t(`routes.status.${status}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("routes.noRoutes")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("devices.name")}</TableHead>
                    <TableHead>{t("reseller.fieldService.vehicle")}</TableHead>
                    <TableHead>{t("routes.date")}</TableHead>
                    <TableHead>{t("routes.stops")}</TableHead>
                    <TableHead>{t("routes.distance")}</TableHead>
                    <TableHead>{t("routes.duration")}</TableHead>
                    <TableHead>{t("routes.statusLabel")}</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((route) => (
                    <TableRow key={route.id} className="cursor-pointer" onClick={() => navigate(`/routes/${route.id}`)}>
                      <TableCell className="font-medium">{route.name}</TableCell>
                      <TableCell>{route.device_id ? deviceById.get(route.device_id)?.name ?? `#${route.device_id}` : "-"}</TableCell>
                      <TableCell>{route.route_date ? new Date(`${route.route_date}T00:00:00`).toLocaleDateString() : "-"}</TableCell>
                      <TableCell>{route.stops.length}</TableCell>
                      <TableCell>{formatDistance(route.distance_m)}</TableCell>
                      <TableCell>{formatDuration(route.duration_s)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={ROUTE_STATUS_BADGE[route.status]}>{t(`routes.status.${route.status}`)}</Badge>
                      </TableCell>
                      <TableCell>
                        {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (confirm(t("reseller.common.deleteConfirm"))) deleteMutation.mutate(route.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
